<template>
  <div class="space-y-6">
    <h1 class="text-2xl font-bold text-gray-800">
      {{ title }}
    </h1>

    <!-- Actions -->
    <div class="flex gap-3">
      <BaseButton type="primary" @click="fetchData(1)">
        {{ $t("refresh") }}
      </BaseButton>

      <BaseButton type="success" @click="openCreateModal">
        + {{ $t("add") }}
      </BaseButton>
    </div>

    <SearchBar v-model="search" @search="fetchData" />

    <!-- Table -->

    <BaseDataTable :columns="columns" :data="items">
      <template #actions="{ row }">
        <TableActions
          :row="row"
          show-edit
          show-delete
          @edit="editItem(row)"
          @delete="deleteItem(row.id)"
        />
      </template>
    </BaseDataTable>

    <BasePagination
      v-if="pagination"
      :pagination="pagination"
      @change-page="fetchData"
    />

    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center"
    >
      <div class="bg-white p-6 rounded-lg w-[420px] space-y-4">
        <h2 class="text-xl font-bold">
          {{ isEdit ? $t("edit_item") : $t("create_item") }}
        </h2>

        <input
          v-model="form.name_en"
          type="text"
          :placeholder="$t('name_en')"
          class="w-full border p-2 rounded"
        />

        <input
          v-model="form.name_ar"
          type="text"
          :placeholder="$t('name_ar')"
          class="w-full border p-2 rounded"
        />

        <input
          v-if="type === 'protection-plans'"
          v-model="form.price_per_day"
          type="number"
          step="0.01"
          :placeholder="$t('price_per_day')"
          class="w-full border p-2 rounded"
        />

        <input
          v-if="type === 'extras'"
          v-model="form.price"
          type="number"
          :placeholder="$t('price')"
          class="w-full border p-2 rounded"
        />

        <div class="flex justify-end gap-2">
          <BaseButton type="secondary" @click="closeModal">
            {{ $t("cancel") }}
          </BaseButton>

          <BaseButton type="success" @click="submitItem">
            {{ isEdit ? $t("update") : $t("create") }}
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import BaseButton from "@/components/Button.vue";
import BaseDataTable from "@/components/BaseDataTable.vue";
import TableActions from "@/components/TableActions.vue";
import BasePagination from "@/components/BasePagination.vue";
import SearchBar from "@/components/SearchBar.vue";

import { alertSuccess, alertError, alertConfirm } from "@/utils/alert";

import masterDataService from "@/services/masterData.service";

export default {
  components: {
    BaseButton,
    BaseDataTable,
    TableActions,
    BasePagination,
    SearchBar,
  },

  data() {
    return {
      items: [],
      pagination: null,
      search: "",

      showModal: false,
      isEdit: false,
      currentId: null,
      submitting: false,

      form: {
        name_en: "",
        name_ar: "",
        price_per_day: "",
        price: "",
      },
    };
  },
  watch: {
    "$route.params.type": {
      immediate: true,
      handler() {
        this.fetchData();
      },
    },
  },

  computed: {
    columns() {
      const cols = [
        { label: this.$t("id"), field: "id" },
        { label: this.$t("name_en"), field: "name_en" },
        { label: this.$t("name_ar"), field: "name_ar" },
      ];

      if (this.type === "protection-plans") {
        cols.push({ label: this.$t("price_per_day"), field: "price_per_day" });
      }

      if (this.type === "extras") {
        cols.push({ label: this.$t("price"), field: "price" });
      }

      return cols;
    },

    type() {
      return this.$route.params.type;
    },

    title() {
      return this.type.replace(/-/g, " ").toUpperCase();
    },
  },

  methods: {
    async fetchData(page = 1) {
      try {
        this.items = [];
        const res = await masterDataService.getAll(this.type, {
          per_page: 10,
          search: this.search,
          page: page,
        });

        // الاستجابة الجديدة تحتوي على داتا مباشرة والباجينيشن بجانبها
        this.items = res.data.data;
        this.pagination = res.data.pagination;
      } catch (error) {
        console.error(error);
        alertError(this.$t("failed_load"));
      }
    },

    openCreateModal() {
      this.isEdit = false;
      this.currentId = null;

      this.form = {
        name_en: "",
        name_ar: "",
        price_per_day: "",
        price: "",
      };

      this.showModal = true;
    },
    editItem(item) {
      this.isEdit = true;
      this.currentId = item.id;

      this.form = {
        name_en: item.name_en,
        name_ar: item.name_ar,
        price_per_day: item.price_per_day || "",
        price: item.price || "",
      };

      this.showModal = true;
    },
    async createItem() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        await masterDataService.create(this.type, this.form);
        alertSuccess(this.$t("created_success"));
        await this.fetchData();
        this.closeModal();
      } catch (error) {
        alertError(this.$t("failed_create"));
      } finally {
        this.submitting = false;
      }
    },
    async updateItem() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        await masterDataService.update(this.type, this.currentId, this.form);
        alertSuccess(this.$t("updated_success"));
        await this.fetchData();
        this.closeModal();
      } catch (error) {
        alertError(this.$t("failed_update"));
      } finally {
        this.submitting = false;
      }
    },
    closeModal() {
      this.showModal = false;
    },
    async submitItem() {
      if (this.isEdit) {
        await this.updateItem();
      } else {
        await this.createItem();
      }
    },

    async deleteItem(id) {
      const result = await alertConfirm(this.$t("delete_confirm"));
      if (!result.isConfirmed) return;

      try {
        await masterDataService.delete(this.type, id);
        this.items = this.items.filter((i) => i.id !== id);
        alertSuccess(this.$t("deleted_success"));
      } catch (error) {
        console.error(error);
        if (error.response?.status === 404) {
          alertError(this.$t("not_found"));
        } else {
          alertError(this.$t("failed_delete"));
        }
      }
    },
  },

  mounted() {
    // fetchData is already called by the watcher on immediate: true
  },
};
</script>
